;(function () {
  // Interactive caliber -> recommended hole size control for loading-block PDPs.
  //
  // Data (assets/caliber-specs.json) shape:
  //   { hole_sizes: [{ name, max_diameter }, ...],
  //     pistol: [{ cartridge, base_diameter, ... }, ...],
  //     rifle:  [ ... ] }
  //
  // Rule: recommend the smallest hole whose max_diameter >= the cartridge's
  // effective base diameter — max(base_diameter, rim_diameter), since rimmed
  // cartridges are widest at the rim. Some diameter values are strings holding one
  // or more numbers (e.g. "0.5820, 0.6040"); we parse every number out and use the
  // largest.

  // Cache the fetch so multiple instances / specs sets share one request per URL.
  const specsCache = new Map()

  function loadSpecs(url) {
    if (!specsCache.has(url)) {
      specsCache.set(
        url,
        fetch(url).then(function (response) {
          if (!response.ok) throw new Error('caliber-specs fetch failed: ' + response.status)
          return response.json()
        }),
      )
    }
    return specsCache.get(url)
  }

  // Pull the effective (largest) diameter out of a number or a messy string.
  function effectiveDiameter(value) {
    if (typeof value === 'number') return value
    if (typeof value !== 'string') return NaN
    const numbers = (value.match(/[\d.]+/g) || []).map(parseFloat).filter(function (n) {
      return !isNaN(n)
    })
    return numbers.length ? Math.max.apply(null, numbers) : NaN
  }

  // The diameter that has to clear the loading-block hole: the larger of the
  // case-head (base) and rim diameters. Rimmed cartridges (rim_diameter present)
  // are widest at the rim; rimless ones omit it and are widest at the base.
  function effectiveBase(entry) {
    const base = effectiveDiameter(entry.base_diameter)
    const rim = effectiveDiameter(entry.rim_diameter)
    if (isNaN(rim)) return base
    if (isNaN(base)) return rim
    return Math.max(base, rim)
  }

  // NaN-aware ascending compare of two numbers. NaN (unparseable/missing) sorts
  // to the end so a bad value never wins. Returns 0 when equal, letting the
  // caller fall through to the next sort dimension.
  function compareNumbers(da, db) {
    const aNaN = isNaN(da)
    const bNaN = isNaN(db)
    if (aNaN && bNaN) return 0
    if (aNaN) return 1
    if (bNaN) return -1
    return da - db
  }

  // Compare a sort dimension, parsing messy string values via effectiveDiameter.
  function compareDimension(a, b) {
    return compareNumbers(effectiveDiameter(a), effectiveDiameter(b))
  }

  // Case length (inches) that splits the two dropdowns. Both controls draw from
  // the combined pistol + rifle data set; the pistol control keeps the shorter
  // cartridges and the rifle control keeps the longer ones. The thresholds
  // overlap (1.36–1.57) on purpose, so borderline cartridges show in both. The
  // rifle floor sits at 1.36 so 300 AAC Blackout (1.368") stays on the rifle side.
  const PISTOL_MAX_CASE_LENGTH = 1.57
  const RIFLE_MIN_CASE_LENGTH = 1.36

  // Merge the pistol + rifle spec arrays and keep the cartridges whose case
  // length fits the requested control. Cartridges with an unparseable/missing
  // case length are dropped — they can't be placed on either side.
  function selectCartridges(specs, type) {
    const all = (specs.pistol || []).concat(specs.rifle || [])
    return all.filter(function (entry) {
      const length = effectiveDiameter(entry.case_length)
      if (isNaN(length)) return false
      return type === 'rifle' ? length >= RIFLE_MIN_CASE_LENGTH : length <= PISTOL_MAX_CASE_LENGTH
    })
  }

  // Smallest hole (by ascending max_diameter) that fits the given diameter.
  // Falls back to the largest hole if nothing is big enough.
  function recommendHole(holeSizes, diameter) {
    const sorted = holeSizes.slice().sort(function (a, b) {
      return a.max_diameter - b.max_diameter
    })
    for (let i = 0; i < sorted.length; i++) {
      if (diameter <= sorted[i].max_diameter) {
        return { hole: sorted[i], oversized: false }
      }
    }
    return { hole: sorted[sorted.length - 1], oversized: true }
  }

  class CaliberCalculator extends HTMLElement {
    connectedCallback() {
      this.select = this.querySelector('[data-caliber-select]')
      this.result = this.querySelector('[data-caliber-result]')
      if (!this.select || !this.result) return

      const url = this.dataset.specsUrl
      const type = this.dataset.type || 'pistol'

      loadSpecs(url)
        .then((specs) => {
          const cartridges = selectCartridges(specs, type)
          if (!cartridges.length) throw new Error('No cartridges for type: ' + type)
          this.holeSizes = specs.hole_sizes || []
          this.byCartridge = {}
          this.populate(cartridges)
          this.select.addEventListener('change', this.onChange.bind(this))
        })
        .catch((error) => {
          // Fail quietly — don't block the page. Hide the control so it isn't left broken.
          console.error('[caliber-calculator]', error)
          this.hidden = true
        })
    }

    populate(cartridges) {
      const fragment = document.createDocumentFragment()
      // Cartridge names start with numbers in several inconsistent formats (9mm,
      // .38, 7.62 x 25), so alphabetical/numeric sorting on the name isn't useful.
      // Sort by physical dimensions instead: bullet_diameter, then effective base
      // (max of base/rim diameter), then overall_length, falling back to a
      // numeric-aware name compare on ties.
      const sorted = cartridges.slice().sort(function (a, b) {
        return (
          compareDimension(a.bullet_diameter, b.bullet_diameter) ||
          compareNumbers(effectiveBase(a), effectiveBase(b)) ||
          compareDimension(a.overall_length, b.overall_length) ||
          String(a.cartridge).localeCompare(String(b.cartridge), undefined, {
            numeric: true,
            sensitivity: 'base',
          })
        )
      })
      sorted.forEach((entry) => {
        const name = entry.cartridge
        // De-dupe repeated cartridge names.
        if (!name || this.byCartridge[name]) return
        this.byCartridge[name] = entry
        const option = document.createElement('option')
        option.value = name
        option.textContent = name
        fragment.appendChild(option)
      })
      this.select.appendChild(fragment)
    }

    onChange() {
      const entry = this.byCartridge[this.select.value]
      if (!entry) {
        this.result.hidden = true
        return
      }

      const diameter = effectiveBase(entry)
      if (isNaN(diameter)) {
        this.result.hidden = true
        return
      }

      const { hole, oversized } = recommendHole(this.holeSizes, diameter)
      this.result.hidden = false
      this.result.innerHTML = oversized
        ? 'This cartridge is larger than our standard holes — please contact us for a custom size.'
        : 'Recommended hole size: <strong>' + hole.name + '</strong>'
    }
  }

  if (!customElements.get('caliber-calculator')) {
    customElements.define('caliber-calculator', CaliberCalculator)
  }
})()
